import { useState, useEffect } from 'react'
import { useQuery, useMutation } from 'react-query'
import { Phone, Upload, Plus, X, PlayCircle, Clock, Users } from 'lucide-react'
import { toast } from 'react-toastify'
import { appsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

function OutboundCalls() {
  const { user } = useAuth()
  const [mode, setMode] = useState('manual') // 'manual' or 'upload'
  const [formData, setFormData] = useState({
    applicationSid: '',
    from: '',
    to: '',
    numbers: [],
    simultaneousCalls: 1,
    timeoutBetweenCalls: 1
  })
  const [manualNumbers, setManualNumbers] = useState([''])
  const [file, setFile] = useState(null)
  const [results, setResults] = useState(null)

  // Fetch available applications
  const { data: appsResponse, isLoading: appsLoading } = useQuery('apps', appsAPI.getApps)
  const apps = appsResponse?.data?.apps || []

  // Single call mutation
  const singleCallMutation = useMutation(
    (data) => fetch('/api/outbound/single', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success('Call initiated successfully!')
          setResults(data)
        } else {
          toast.error(data.message || 'Failed to initiate call')
        }
      },
      onError: (error) => {
        toast.error('Failed to initiate call')
        console.error('Call error:', error)
      }
    }
  )

  // Bulk call mutation
  const bulkCallMutation = useMutation(
    (data) => fetch('/api/outbound/bulk', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success('Bulk calls initiated successfully!')
          setResults(data.results)
        } else {
          toast.error(data.message || 'Failed to initiate bulk calls')
        }
      },
      onError: (error) => {
        toast.error('Failed to initiate bulk calls')
        console.error('Bulk call error:', error)
      }
    }
  )

  // File upload mutation
  const uploadMutation = useMutation(
    (formData) => fetch('/api/outbound/upload', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    }).then(res => res.json()),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success('File processed and calls initiated!')
          setResults(data.results)
        } else {
          toast.error(data.message || 'Failed to process file')
        }
      },
      onError: (error) => {
        toast.error('Failed to process file')
        console.error('Upload error:', error)
      }
    }
  )

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const addManualNumber = () => {
    setManualNumbers(prev => [...prev, ''])
  }

  const removeManualNumber = (index) => {
    setManualNumbers(prev => prev.filter((_, i) => i !== index))
  }

  const updateManualNumber = (index, value) => {
    setManualNumbers(prev => prev.map((num, i) => i === index ? value : num))
  }

  const handleSingleCall = () => {
    if (!formData.applicationSid || !formData.from || !formData.to) {
      toast.error('Please fill in all required fields')
      return
    }

    singleCallMutation.mutate({
      applicationSid: formData.applicationSid,
      from: formData.from,
      to: formData.to
    })
  }

  const handleBulkCall = () => {
    const validNumbers = manualNumbers.filter(num => num.trim())
    
    if (!formData.applicationSid || !formData.from || validNumbers.length === 0) {
      toast.error('Please fill in all required fields and add at least one phone number')
      return
    }

    bulkCallMutation.mutate({
      applicationSid: formData.applicationSid,
      from: formData.from,
      numbers: validNumbers,
      simultaneousCalls: parseInt(formData.simultaneousCalls),
      timeoutBetweenCalls: parseInt(formData.timeoutBetweenCalls)
    })
  }

  const handleFileUpload = () => {
    if (!formData.applicationSid || !formData.from || !file) {
      toast.error('Please fill in all required fields and select a file')
      return
    }

    const uploadFormData = new FormData()
    uploadFormData.append('file', file)
    uploadFormData.append('applicationSid', formData.applicationSid)
    uploadFormData.append('from', formData.from)
    uploadFormData.append('simultaneousCalls', formData.simultaneousCalls)
    uploadFormData.append('timeoutBetweenCalls', formData.timeoutBetweenCalls)

    uploadMutation.mutate(uploadFormData)
  }

  if (appsLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-primary-900">Outbound Calls</h1>
          <p className="mt-2 text-sm text-primary-600">
            Create single or bulk outbound calls to your contacts.
          </p>
        </div>
      </div>

      {/* Mode Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-6">
          <label className="text-base font-medium text-primary-900">Call Method</label>
          <p className="text-sm leading-5 text-primary-500">Choose how you want to initiate calls</p>
          <fieldset className="mt-4">
            <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
              <div className="flex items-center">
                <input
                  id="manual"
                  name="mode"
                  type="radio"
                  checked={mode === 'manual'}
                  onChange={() => setMode('manual')}
                  className="h-4 w-4 border-primary-300 text-primary-600 focus:ring-primary-600"
                />
                <label htmlFor="manual" className="ml-3 block text-sm font-medium leading-6 text-primary-900">
                  Manual Entry
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="upload"
                  name="mode"
                  type="radio"
                  checked={mode === 'upload'}
                  onChange={() => setMode('upload')}
                  className="h-4 w-4 border-primary-300 text-primary-600 focus:ring-primary-600"
                />
                <label htmlFor="upload" className="ml-3 block text-sm font-medium leading-6 text-primary-900">
                  File Upload (CSV/Excel)
                </label>
              </div>
            </div>
          </fieldset>
        </div>

        {/* Common Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              Application *
            </label>
            <select
              name="applicationSid"
              value={formData.applicationSid}
              onChange={handleInputChange}
              className="input-field"
              required
            >
              <option value="">Select Application</option>
              {apps?.map((app) => (
                <option key={app._id} value={app.jambonzId}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              From Number *
            </label>
            <input
              type="text"
              name="from"
              value={formData.from}
              onChange={handleInputChange}
              className="input-field"
              placeholder="e.g., 15083728399"
              required
            />
          </div>
        </div>

        {/* Manual Entry Mode */}
        {mode === 'manual' && (
          <div className="space-y-6">
            {/* Single Call Option */}
            <div className="border border-primary-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-primary-900 mb-4">Single Call</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    To Number *
                  </label>
                  <input
                    type="text"
                    name="to"
                    value={formData.to}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="e.g., 15082084809"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleSingleCall}
                    disabled={singleCallMutation.isLoading}
                    className="btn-primary w-full flex items-center justify-center"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {singleCallMutation.isLoading ? 'Calling...' : 'Make Call'}
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Calls Option */}
            <div className="border border-primary-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-primary-900 mb-4">Bulk Calls</h3>
              
              {/* Phone Numbers List */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Phone Numbers
                </label>
                {manualNumbers.map((number, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={number}
                      onChange={(e) => updateManualNumber(index, e.target.value)}
                      className="input-field flex-1"
                      placeholder="e.g., 15082084809"
                    />
                    {manualNumbers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeManualNumber(index)}
                        className="btn-secondary p-2 flex items-center justify-center"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addManualNumber}
                  className="btn-secondary text-sm flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Number
                </button>
              </div>

              {/* Bulk Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Simultaneous Calls
                  </label>
                  <input
                    type="number"
                    name="simultaneousCalls"
                    value={formData.simultaneousCalls}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Timeout Between Calls (seconds)
                  </label>
                  <input
                    type="number"
                    name="timeoutBetweenCalls"
                    value={formData.timeoutBetweenCalls}
                    onChange={handleInputChange}
                    min="0"
                    className="input-field"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleBulkCall}
                    disabled={bulkCallMutation.isLoading}
                    className="btn-primary w-full flex items-center justify-center"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    {bulkCallMutation.isLoading ? 'Calling...' : 'Start Bulk Calls'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Mode */}
        {mode === 'upload' && (
          <div className="border border-primary-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-primary-900 mb-4">File Upload</h3>
            
            {/* File Upload */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-primary-700">
                  Upload CSV or Excel File
                </label>
                <a 
                  href="/sample_numbers.csv" 
                  download 
                  className="text-xs text-primary-600 hover:text-primary-800 underline"
                >
                  Download Sample CSV
                </a>
              </div>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-primary-300 border-dashed rounded-lg cursor-pointer bg-primary-50 hover:bg-primary-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-4 text-primary-500" />
                    <p className="mb-2 text-sm text-primary-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-primary-500">CSV or Excel files only</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </label>
              </div>
              {file && (
                <p className="mt-2 text-sm text-primary-600">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {/* Upload Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-1">
                  Simultaneous Calls
                </label>
                <input
                  type="number"
                  name="simultaneousCalls"
                  value={formData.simultaneousCalls}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-1">
                  Timeout Between Calls (seconds)
                </label>
                <input
                  type="number"
                  name="timeoutBetweenCalls"
                  value={formData.timeoutBetweenCalls}
                  onChange={handleInputChange}
                  min="0"
                  className="input-field"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleFileUpload}
                  disabled={uploadMutation.isLoading}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadMutation.isLoading ? 'Processing...' : 'Process File'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-primary-900 mb-4">Call Results</h3>
          
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-600">Total</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{results.total || 1}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Phone className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-600">Successful</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{results.successful || (results.call ? 1 : 0)}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <X className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-sm font-medium text-red-600">Failed</span>
              </div>
              <p className="text-2xl font-bold text-red-900">{results.failed || 0}</p>
            </div>
          </div>

          {/* Detailed Results */}
          {results.calls && results.calls.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-primary-900 mb-3">Call Details</h4>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-primary-300">
                  <thead className="bg-primary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                        Call ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-primary-200">
                    {results.calls.map((call, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-900">
                          {call.number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            call.success 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {call.success ? 'Success' : 'Failed'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                          {call.callId || call.sid || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Single Call Result */}
          {results.call && (
            <div>
              <h4 className="text-md font-medium text-primary-900 mb-3">Call Created</h4>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Call ID:</strong> {results.call.callId}
                </p>
                <p className="text-sm text-green-800">
                  <strong>SID:</strong> {results.call.sid}
                </p>
              </div>
            </div>
          )}

          {/* Errors */}
          {results.errors && results.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-md font-medium text-red-900 mb-3">Errors</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                {results.errors.map((error, index) => (
                  <p key={index} className="text-sm text-red-800">
                    <strong>{error.number}:</strong> {error.error}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OutboundCalls